import React, { FC, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentType,
  ChatSuggestion,
  GetChatSuggestionsResponse,
  UserInputResponse,
} from '@dreamai/types';

import api from '../../../api';
import CreateCampaignModal from './CreateCampaignModal';
import ErrorState from '../../common/ErrorState';
import ThreadShareModal from '../sidebar/ThreadShareModal';
import { ReactComponent as ArrowRightIcon } from '../../../icons/arrow-right.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as CheckIcon } from '../../../icons/check.svg';
import { ReactComponent as PlusIcon } from '../../../icons/plus.svg';
import { ReactComponent as SpinnerIcon } from '../../../icons/spinner.svg';
import './new-thread-modal.scss';

const getPlaceholderForAgentType = (agentType?: string): string => {
  switch (agentType) {
    case AgentType.SalesManager:
      return 'E.g. Discuss strategy...';
    case AgentType.BusinessManager:
      return 'E.g. Holiday research';
    default:
      return 'E.g. New project...';
  }
};

interface NewThreadModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentId: string;
  agentName?: string;
  agentImageUrl?: string;
  agentType?: string;
  onThreadCreated?: () => void;
}

const NewThreadModal: FC<NewThreadModalProps> = ({
  isOpen,
  onClose,
  agentId,
  agentName,
  agentImageUrl,
  agentType,
  onThreadCreated,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<ChatSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [createdThreadId, setCreatedThreadId] = useState<string | null>(null);
  const [hasCompanyUsers, setHasCompanyUsers] = useState(false);

  // Fetch suggestions when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchSuggestions = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const response = await api.get<GetChatSuggestionsResponse>(
          '/agents/chat-suggestions',
          {
            params: { agent_id: agentId },
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (response.data.success) {
          setSuggestions(response.data.suggestions);
        }
      } catch (err) {
        console.error('Error fetching chat suggestions:', err);
      } finally {
        setLoading(false);
      }
    };

    const fetchCompanyUsers = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await api.get('/agents/users/company', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success && response.data.users?.length > 0) {
          setHasCompanyUsers(true);
        }
      } catch (err) {
        console.error('Error fetching company users:', err);
      }
    };

    fetchSuggestions();
    fetchCompanyUsers();
    // Focus input when modal opens
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, agentId, getAccessTokenSilently]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setInputValue('');
      setSuggestions([]);
      setSubmitting(false);
      setCreatedThreadId(null);
      setHasCompanyUsers(false);
    }
  }, [isOpen]);

  const handleSubmit = async (threadName: string) => {
    if (!threadName.trim()) return;

    try {
      setSubmitting(true);
      const tightThreadName = threadName.trim();
      const token = await getAccessTokenSilently();
      const response = await api.post<UserInputResponse>(
        '/agents',
        {
          agent_id: agentId,
          message: `SYSTEM_MESSAGE The user will send you the first message in a minute they have named this thread ${tightThreadName}. Send a very quick and short message appropriate to that thread name asking them what they want to discuss/do etc, do not call any agents in this first response.`,
          thread_name: tightThreadName,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success && response.data.thread_id) {
        onThreadCreated?.();
        setSubmitting(false);

        // Skip share step if no other company users
        if (!hasCompanyUsers) {
          onClose();
          navigate(
            `/conversations?agent_id=${agentId}&thread_id=${response.data.thread_id}&new_chat=true`,
          );
        } else {
          setCreatedThreadId(response.data.thread_id);
        }
      }
    } catch (err) {
      console.error('Error creating thread:', err);
      setSubmitting(false);
      setError(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(inputValue);
    }
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleSuggestionClick = (suggestion: ChatSuggestion) => {
    if (suggestion.specific_action) {
      // Suggestion with action - submit immediately
      handleSubmit(suggestion.text);
    } else {
      // Just fill the input for editing
      setInputValue(suggestion.text);
      inputRef.current?.focus();
    }
  };

  if (!isOpen && !showCampaignModal) return null;

  return (
    <>
      <CreateCampaignModal
        show={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        agentName={agentName}
      />
      {isOpen &&
        createPortal(
          <div className='new-thread-modal-overlay' onClick={onClose}>
            <div
              className={`new-thread-modal ${submitting ? 'loading' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className='new-thread-modal-content'>
                {createdThreadId ? (
                  <>
                    <h4>(Optional)</h4>
                    <h1>Share new chat</h1>
                    <ThreadShareModal
                      threadId={createdThreadId}
                      isOwner={true}
                      isViaCompany={false}
                      onClose={onClose}
                      variant='newThread'
                      agentId={agentId}
                    />
                  </>
                ) : (
                  <>
                    <h1>Create a new chat</h1>
                    <div className='new-thread-input-wrapper'>
                      <div className='agent-avatar'>
                        {submitting ? (
                          <SpinnerIcon className='avatar-spinner' />
                        ) : (
                          agentImageUrl && (
                            <img src={agentImageUrl} alt={agentName} />
                          )
                        )}
                      </div>
                      <div className='new-thread-form-wrapper'>
                        <label className='new-thread-label'>Chat name</label>
                        <textarea
                          ref={inputRef}
                          className='new-thread-input'
                          placeholder={getPlaceholderForAgentType(agentType)}
                          value={inputValue}
                          maxLength={60}
                          onChange={(e) => {
                            setInputValue(e.target.value);
                            // Auto-resize - reset to 0 to get accurate scrollHeight
                            e.target.style.height = '0px';
                            e.target.style.height = `${e.target.scrollHeight}px`;
                          }}
                          onKeyDown={handleKeyDown}
                          disabled={submitting}
                          rows={1}
                        />
                      </div>
                      <button
                        className={`new-thread-submit-btn ${inputValue.trim() ? 'has-text' : ''}`}
                        onClick={() => handleSubmit(inputValue)}
                        disabled={submitting || !inputValue.trim()}
                      >
                        <CheckIcon />
                      </button>
                    </div>

                    {submitting && (
                      <div className='new-thread-loading-spinner' />
                    )}

                    {error && (
                      <ErrorState
                        title='Something went wrong'
                        subtitle='Please try again'
                      />
                    )}

                    {!submitting &&
                      !error &&
                      suggestions.filter(
                        (s) => s.chatName && !s.specific_action,
                      ).length > 0 && (
                        <div className='new-thread-suggestions'>
                          {suggestions
                            .filter((s) => s.chatName && !s.specific_action)
                            .map((suggestion, index) => (
                              <button
                                key={index}
                                className='suggestion-pill'
                                onClick={() =>
                                  handleSubmit(suggestion.chatName || '')
                                }
                              >
                                {suggestion.text}
                              </button>
                            ))}
                        </div>
                      )}

                    {!submitting &&
                      !error &&
                      suggestions.filter((s) => s.specific_action).length >
                        0 && (
                        <>
                          <h4 className='new-thread-or'>OR</h4>
                          <div className='new-thread-actions'>
                            {suggestions
                              .filter((s) => s.specific_action)
                              .map((suggestion, index) => (
                                <button
                                  key={index}
                                  className='action-item'
                                  onClick={() => {
                                    if (
                                      suggestion.specific_action ===
                                      'create_campaign'
                                    ) {
                                      onClose();
                                      setShowCampaignModal(true);
                                    }
                                  }}
                                >
                                  <span className='action-icon'>
                                    <CampaignIcon />
                                  </span>
                                  <span className='action-text'>
                                    {suggestion.text}
                                  </span>
                                  <span className='action-arrow'>
                                    <ArrowRightIcon />
                                  </span>
                                </button>
                              ))}
                          </div>
                        </>
                      )}
                  </>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default NewThreadModal;
